// Growth › Lead finder › Runs: /growth/finder/runs and /growth/finder/runs/:id
//
// The run log: what ran, what it read, what it refused and why. A page follows
// a run that's going, and stops asking once it ends.

import { useParams, useSearchParams } from 'react-router-dom'
import { api } from '@/api'
import { errorMessage, isApiError } from '@/api/client'
import type { FinderPageLog, FinderRun } from '@/api/growthInsights'
import { PageHeader } from '@/components/shell/PageHeader'
import { Button, ButtonLink, Card, CardHeader, DataTable, EmptyState, ErrorState, InlineAlert, LoadingState, Pagination, RelativeTime, SkeletonRows, StatTile, useToast, type Column } from '@/components/ui'
import { formatCount, formatDateTime } from '@/lib/format'
import { SOURCE_LABELS } from '@/lib/growthInsights'
import { useDocumentTitle } from '@/lib/hooks'
import { invalidateQueries, useQuery } from '@/lib/query'
import { usePoll } from '@/lib/useFinder'
import { FinderFrame, OutcomeBadge, RunStatusBadge } from './finderShared'
import { SafeExternalLink } from './shared'

const PAGE_SIZE = 25

const sourceLabel = (run: FinderRun) => SOURCE_LABELS[run.adapter] ?? run.adapter

export default function FinderRunsPage() {
  useDocumentTitle('Lead finder runs')
  const [params, setParams] = useSearchParams()
  const raw = Number(params.get('page'))
  const page = Number.isInteger(raw) && raw > 0 ? raw : 1
  const { data, error, status, refetch } = useQuery(['growth', 'runs', page], (signal) => api.growth.finder.runs({ page, page_size: PAGE_SIZE }, signal), { keepPrevious: true })
  usePoll(!!data?.runs.some((r) => r.status === 'running'), refetch)

  const columns: Column<FinderRun>[] = [
    { id: 'started', header: 'Started', primary: true, cell: (r) => <RelativeTime value={r.started_at} /> },
    { id: 'source', header: 'Source', cell: (r) => <span className="text-muted">{sourceLabel(r)}{r.trigger === 'schedule' ? ' (scheduled)' : ''}</span> },
    { id: 'status', header: 'Status', cell: (r) => <RunStatusBadge status={r.status} /> },
    { id: 'read', header: 'Read', align: 'right', cell: (r) => <span className="font-mono text-xs tabular">{r.pages_fetched}</span> },
    { id: 'refused', header: 'Not read', align: 'right', cell: (r) => <span className="font-mono text-xs tabular text-muted">{r.pages_refused}</span> },
    { id: 'found', header: 'Found', align: 'right', cell: (r) => <span className="font-mono text-xs tabular">{r.candidates_found}</span> },
    { id: 'duplicates', header: 'Already leads', align: 'right', cell: (r) => <span className="font-mono text-xs tabular text-muted">{r.duplicates}</span> },
    { id: 'suppressed', header: 'Do not contact', align: 'right', cell: (r) => <span className="font-mono text-xs tabular text-muted">{r.suppressed}</span> },
  ]

  return (
    <FinderFrame>
      <Card flush className="overflow-hidden">
        {status === 'loading' ? (
          <SkeletonRows rows={5} label="Loading runs" />
        ) : status === 'error' && !data ? (
          <ErrorState error={error} title="Couldn't load the runs" onRetry={refetch} />
        ) : !data || data.runs.length === 0 ? (
          <EmptyState title="No runs yet" description="Start a run to find leagues." />
        ) : (
          <>
            <DataTable caption="Runs" rows={data.runs} columns={columns} rowKey={(r) => r.id} rowHref={(r) => `/growth/finder/runs/${r.id}`} />
            <div className="border-t border-line px-4 py-3">
              <Pagination page={data.page} pageSize={data.page_size} total={data.total} noun="runs" onPageChange={(p) => setParams(p === 1 ? {} : { page: String(p) }, { replace: true })} />
            </div>
          </>
        )}
      </Card>
    </FinderFrame>
  )
}

export function FinderRunDetailPage() {
  const { id: rawId } = useParams()
  const id = Number(rawId)
  const validId = Number.isInteger(id) && id > 0
  const toast = useToast()
  const { data, error, status, refetch } = useQuery(validId ? ['growth', 'run', id] : null, (signal) => api.growth.finder.run(id, signal))
  useDocumentTitle(validId ? `Run #${id}` : 'Run')
  const running = data?.run.status === 'running'
  usePoll(running, refetch)

  if (!validId || (status === 'error' && isApiError(error) && error.code === 'not_found')) {
    return (
      <Card>
        <EmptyState title="Run not found" description={validId ? `There's no run with id ${id}.` : `“${rawId}” isn't a run id.`} action={<ButtonLink to="/growth/finder/runs">Back to runs</ButtonLink>} />
      </Card>
    )
  }
  if (status === 'loading' && !data) return <LoadingState label="Loading run…" />
  if (!data) {
    return (
      <Card>
        <ErrorState error={error} title="Couldn't load this run" onRetry={refetch} />
      </Card>
    )
  }

  const { run, pages } = data
  const checksLeads = run.adapter === 'activity'
  const stop = async () => {
    try {
      await api.growth.finder.stopRun(run.id)
      invalidateQueries('growth')
      toast.success('It stops after the page it is on.')
    } catch (err) {
      toast.error(errorMessage(err))
    }
  }

  const columns: Column<FinderPageLog>[] = [
    { id: 'url', header: 'Page', primary: true, cell: (p) => (p.url.startsWith('http') ? <SafeExternalLink href={p.url} /> : <span className="break-words">{p.url}</span>), className: 'min-w-[14rem] max-w-[24rem]' },
    { id: 'outcome', header: 'Outcome', cell: (p) => <OutcomeBadge outcome={p.outcome} /> },
    { id: 'detail', header: 'Detail', cell: (p) => <span className="text-muted">{p.detail ?? '—'}</span> },
  ]

  return (
    <div className="grid grid-cols-1 gap-5">
      <PageHeader
        crumbs={[{ label: 'Lead finder', to: '/growth/finder/queue' }, { label: 'Runs', to: '/growth/finder/runs' }, { label: `Run #${run.id}` }]}
        title={`Run #${run.id}`}
        subtitle={`${sourceLabel(run)}, started ${formatDateTime(run.started_at)}`}
        badges={<RunStatusBadge status={run.status} />}
        actions={
          running ? (
            <Button disabled={run.stop_requested} onClick={() => void stop()}>
              {run.stop_requested ? 'Stopping…' : 'Stop'}
            </Button>
          ) : (
            <ButtonLink to={checksLeads ? '/growth/leads' : '/growth/finder/queue'}>{checksLeads ? 'Leads' : 'Review queue'}</ButtonLink>
          )
        }
      />
      {run.note ? <InlineAlert tone={run.status === 'failed' ? 'bad' : checksLeads && run.status === 'done' ? 'info' : 'warn'}>{run.note}</InlineAlert> : null}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        <StatTile label="Pages read" value={formatCount(run.pages_fetched)} />
        <StatTile label="Not read" value={formatCount(run.pages_refused)} hint="robots.txt, a login, the budget…" />
        {checksLeads ? (
          <StatTile label="Leads checked" value={`${formatCount(run.items_done)} of ${formatCount(run.items_total)}`} />
        ) : (
          <>
            <StatTile label="Found" value={formatCount(run.candidates_found)} />
            <StatTile label="Already leads" value={formatCount(run.duplicates)} />
            <StatTile label="Do not contact" value={formatCount(run.suppressed)} hint="Dropped, not stored" />
          </>
        )}
      </div>
      <Card flush className="overflow-hidden">
        <div className="px-4 pt-4">
          <CardHeader title="Pages" description="Every page it tried, and what became of it. The pages themselves aren't kept." />
        </div>
        {pages.length === 0 ? <EmptyState title="Nothing yet" description={running ? 'The first page is on its way.' : 'This run read no pages.'} /> : <DataTable caption="Pages" rows={pages} columns={columns} rowKey={(p) => p.id} />}
      </Card>
    </div>
  )
}
